import React, { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Modal,
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Divider,
  LinearProgress,
} from "@mui/material";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ReplayIcon from "@mui/icons-material/Replay";
import Webcam from "react-webcam";
import { FilesetResolver, FaceLandmarker } from "@mediapipe/tasks-vision";
import {
  loadRecaptcha,
  renderRecaptcha,
  getRecaptchaToken,
  resetRecaptcha,
  isRecaptchaConfigured,
} from "../utils/recaptcha";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const STEPS = ["Security Check", "Liveness Photo", "Submit"];

const modalBoxSx = {
  width: 560,
  maxHeight: "90vh",
  overflow: "auto",
  bgcolor: "#ffffff",
  borderRadius: "14px",
  boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
};

const CHALLENGE_DEFS = {
  turn_left: { id: "turn_left", label: "Turn your head to the left" },
  turn_right: { id: "turn_right", label: "Turn your head to the right" },
  smile: { id: "smile", label: "Smile for the camera" },
};

const CHALLENGE_ORDER = ["turn_left", "turn_right", "smile"];
const CONFIRM_FRAMES_NEEDED = 15;
const COOLDOWN_BETWEEN_CHALLENGES = 45;

// ── Landmark math helpers ────────────────────────────────────────────────────

function landmarkDistance(a, b) {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}

function estimateYaw(landmarks) {
  const noseTip = landmarks[1];
  const leftCheek = landmarks[234];
  const rightCheek = landmarks[454];
  const dLeft = landmarkDistance(noseTip, leftCheek);
  const dRight = landmarkDistance(noseTip, rightCheek);
  const total = dLeft + dRight;
  if (total === 0) return 0;
  return (dRight - dLeft) / total;
}

function detectSmile(landmarks) {
  const mouthLeft = landmarks[61];
  const mouthRight = landmarks[291];
  const upperLip = landmarks[13];
  const lowerLip = landmarks[14];
  const mouthWidth = landmarkDistance(mouthLeft, mouthRight);
  const mouthOpen = landmarkDistance(upperLip, lowerLip);
  if (mouthWidth === 0) return 0;
  const ratio = mouthOpen / mouthWidth;
  const leftEyeInner = landmarks[133];
  const rightEyeInner = landmarks[362];
  const eyeDist = landmarkDistance(leftEyeInner, rightEyeInner);
  if (eyeDist === 0) return 0;
  const widthRatio = mouthWidth / eyeDist;
  return Math.min(1, Math.max(0, (widthRatio - 0.85) / 0.35 + ratio * 0.5));
}

function evaluateChallenge(landmarks, challengeId) {
  if (!landmarks || landmarks.length < 468) return false;
  switch (challengeId) {
    case "turn_left": {
      const yaw = estimateYaw(landmarks);
      return yaw < -0.18;
    }
    case "turn_right": {
      const yaw = estimateYaw(landmarks);
      return yaw > 0.18;
    }
    case "smile": {
      const score = detectSmile(landmarks);
      return score > 0.45;
    }
    default:
      return false;
  }
}

function calcProgress(landmarks, challengeId) {
  if (!landmarks || landmarks.length < 468) return 0;
  switch (challengeId) {
    case "turn_left": {
      const yaw = estimateYaw(landmarks);
      return Math.min(1, Math.max(0, -yaw / 0.18));
    }
    case "turn_right": {
      const yaw = estimateYaw(landmarks);
      return Math.min(1, Math.max(0, yaw / 0.18));
    }
    case "smile": {
      const score = detectSmile(landmarks);
      return Math.min(1, Math.max(0, score / 0.45));
    }
    default:
      return 0;
  }
}

// ── Auth helper ──────────────────────────────────────────────────────────────

async function getAuthHeaders() {
  const { getAuth } = await import("firebase/auth");
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) return {};
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

// ── Component ────────────────────────────────────────────────────────────────

export default function ManualVerificationModal({ isOpen, onClose }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifyingRecaptcha, setVerifyingRecaptcha] = useState(false);

  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRendered = useRef(false);

  const webcamRef = useRef(null);
  const canvasRef = useRef(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [faceDetected, setFaceDetected] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const landmarkerRef = useRef(null);
  const animFrameRef = useRef(null);

  const [challengeIndex, setChallengeIndex] = useState(0);
  const [challengeOrder, setChallengeOrder] = useState([...CHALLENGE_ORDER]);
  const [progress, setProgress] = useState(0);
  const [allChallengesDone, setAllChallengesDone] = useState(false);
  const confirmCountRef = useRef(0);
  const cooldownRef = useRef(0);
  const [webcamKey, setWebcamKey] = useState(0);

  const [submissionStatus, setSubmissionStatus] = useState(null);

  const currentChallengeId = challengeOrder[challengeIndex] ?? null;
  const currentChallenge = currentChallengeId ? CHALLENGE_DEFS[currentChallengeId] : null;
  const totalChallenges = CHALLENGE_ORDER.length;
  const challengeIdRef = useRef(currentChallengeId);
  const challengeIndexRef = useRef(challengeIndex);
  challengeIdRef.current = currentChallengeId;
  challengeIndexRef.current = challengeIndex;

  // Initialize challenge sequence when entering step 1
  useEffect(() => {
    if (step === 1) {
      setChallengeIndex(0);
      setChallengeOrder([...CHALLENGE_ORDER].sort(() => Math.random() - 0.5));
      setAllChallengesDone(false);
      confirmCountRef.current = 0;
      cooldownRef.current = COOLDOWN_BETWEEN_CHALLENGES;
      setProgress(0);
    }
  }, [step]);

  // Step 0: reCAPTCHA — container is always in DOM (hidden when not on step 0)
  useEffect(() => {
    if (!isOpen || step !== 0) return;
    let cancelled = false;

    async function init() {
      if (!isRecaptchaConfigured()) {
        setRecaptchaReady(true);
        setRecaptchaToken("dev-bypass");
        return;
      }
      try {
        await loadRecaptcha();
        if (cancelled) return;
        setTimeout(async () => {
          if (cancelled || recaptchaRendered.current) return;
          await renderRecaptcha("manual-verify-recaptcha", {
            action: "LOGIN",
            callback: (token) => {
              setRecaptchaToken(token);
              setRecaptchaReady(true);
            },
            expiredCallback: () => {
              setRecaptchaToken(null);
              setRecaptchaReady(false);
            },
          });
          recaptchaRendered.current = true;
        }, 300);
      } catch {
        if (!cancelled) setError("Could not load security check.");
      }
    }
    init();
    return () => { cancelled = true; };
  }, [isOpen, step]);

  // Step 1: MediaPipe FaceLandmarker + challenge detection loop
  useEffect(() => {
    if (step !== 1 || !cameraReady || allChallengesDone) return;
    let cancelled = false;

    async function initLandmarker() {
      try {
        const vision = await FilesetResolver.forVisionTasks(
          "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm"
        );
        if (cancelled) return;
        const landmarker = await FaceLandmarker.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath:
              "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task",
            delegate: "GPU",
          },
          runningMode: "VIDEO",
          numFaces: 1,
          outputFaceBlendshapes: false,
          outputFacialTransformationMatrixes: false,
        });
        if (cancelled) {
          landmarker.close();
          return;
        }
        landmarkerRef.current = landmarker;
        detectLoop();
      } catch (err) {
        if (!cancelled) setError("Could not initialize face detection.");
      }
    }

    function detectLoop() {
      if (cancelled) return;
      const detector = landmarkerRef.current;
      if (!detector) return;
      const video = webcamRef.current?.video;
      if (!video || video.readyState < 2) {
        animFrameRef.current = requestAnimationFrame(detectLoop);
        return;
      }

      const cid = challengeIdRef.current;
      if (!cid) {
        animFrameRef.current = requestAnimationFrame(detectLoop);
        return;
      }

      try {
        const result = detector.detectForVideo(video, performance.now());
        const hasFace = result.faceLandmarks && result.faceLandmarks.length === 1;
        setFaceDetected(hasFace);

        if (hasFace) {
          const lm = result.faceLandmarks[0];
          drawLandmarks(lm, video);

          if (cooldownRef.current > 0) {
            cooldownRef.current -= 1;
            setProgress(0);
            animFrameRef.current = requestAnimationFrame(detectLoop);
            return;
          }

          const prog = calcProgress(lm, cid);
          setProgress(Math.round(prog * 100));

          if (evaluateChallenge(lm, cid)) {
            confirmCountRef.current += 1;
            if (confirmCountRef.current >= CONFIRM_FRAMES_NEEDED) {
              const nextIndex = challengeIndexRef.current + 1;
              if (nextIndex >= CHALLENGE_ORDER.length) {
                setAllChallengesDone(true);
                autoCapture(video);
                return;
              }
              confirmCountRef.current = 0;
              cooldownRef.current = COOLDOWN_BETWEEN_CHALLENGES;
              setChallengeIndex(nextIndex);
              setProgress(0);
            }
          } else {
            confirmCountRef.current = Math.max(0, confirmCountRef.current - 1);
          }
        } else {
          confirmCountRef.current = 0;
          setProgress(0);
          clearCanvas();
        }
      } catch {
        // frame skipped
      }

      animFrameRef.current = requestAnimationFrame(detectLoop);
    }

    initLandmarker();

    return () => {
      cancelled = true;
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      if (landmarkerRef.current) {
        landmarkerRef.current.close();
        landmarkerRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, cameraReady, allChallengesDone]);

  // ── Canvas drawing ─────────────────────────────────────────────────────────

  const drawLandmarks = useCallback((landmarks, video) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
    }
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.fillStyle = "rgba(4,120,87,0.6)";
    const keyPoints = [1, 33, 263, 61, 291, 13, 14, 133, 362, 234, 454, 10, 152];
    for (const idx of keyPoints) {
      const pt = landmarks[idx];
      if (!pt) continue;
      ctx.beginPath();
      ctx.arc(pt.x * canvas.width, pt.y * canvas.height, 3, 0, 2 * Math.PI);
      ctx.fill();
    }

    ctx.strokeStyle = "rgba(4,120,87,0.3)";
    ctx.lineWidth = 1;
    const FACE_OVAL = [10,338,297,332,284,251,389,356,454,323,361,288,397,365,379,378,400,377,152,148,176,149,150,136,172,58,132,93,234,127,162,21,54,103,67,109,10];
    ctx.beginPath();
    for (let i = 0; i < FACE_OVAL.length; i++) {
      const pt = landmarks[FACE_OVAL[i]];
      if (!pt) continue;
      const x = pt.x * canvas.width;
      const y = pt.y * canvas.height;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }, []);

  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }, []);

  // ── Auto-capture when challenge is completed ───────────────────────────────

  const autoCapture = useCallback((video) => {
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    const tempCanvas = document.createElement("canvas");
    tempCanvas.width = video.videoWidth;
    tempCanvas.height = video.videoHeight;
    const ctx = tempCanvas.getContext("2d");
    ctx.translate(tempCanvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0);
    setCapturedImage(tempCanvas.toDataURL("image/jpeg", 0.9));
  }, []);

  const handleContinueToLiveness = async () => {
    const token = recaptchaToken || getRecaptchaToken();
    if (!token) {
      setError("Please complete the security check above.");
      return;
    }
    setVerifyingRecaptcha(true);
    setError("");
    try {
      const headers = await getAuthHeaders();
      headers["Content-Type"] = "application/json";
      const res = await fetch(`${API_BASE_URL}/api/manual-verification/verify-recaptcha/`, {
        method: "POST",
        headers,
        body: JSON.stringify({ recaptcha_token: token }),
        credentials: "include",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || "Security check failed. Please try again.");
        resetRecaptcha();
        setRecaptchaToken(null);
        setRecaptchaReady(false);
        return;
      }
      setStep(1);
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setVerifyingRecaptcha(false);
    }
  };

  const handleRetake = () => {
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (landmarkerRef.current) {
      landmarkerRef.current.close();
      landmarkerRef.current = null;
    }
    setCapturedImage(null);
    setFaceDetected(false);
    setAllChallengesDone(false);
    setChallengeIndex(0);
    setChallengeOrder([...CHALLENGE_ORDER].sort(() => Math.random() - 0.5));
    confirmCountRef.current = 0;
    cooldownRef.current = COOLDOWN_BETWEEN_CHALLENGES;
    setProgress(0);
    setCameraReady(false);
    setWebcamKey((k) => k + 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError("");
    try {
      const blob = await (await fetch(capturedImage)).blob();
      const file = new File([blob], "liveness_photo.jpg", { type: "image/jpeg" });
      const formData = new FormData();
      formData.append("photo", file);

      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE_URL}/api/manual-verification/submit/`, {
        method: "POST",
        headers,
        body: formData,
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) {
        const msg = data.error || "Submission failed. Please try again.";
        if (msg.toLowerCase().includes("security") || msg.toLowerCase().includes("expired") || msg.toLowerCase().includes("recaptcha")) {
          setError("Security check expired. Please complete it again.");
          setCapturedImage(null);
          setFaceDetected(false);
          setAllChallengesDone(false);
          setChallengeIndex(0);
          setCameraReady(false);
          confirmCountRef.current = 0;
          cooldownRef.current = COOLDOWN_BETWEEN_CHALLENGES;
          setProgress(0);
          setRecaptchaReady(false);
          setRecaptchaToken(null);
          recaptchaRendered.current = false;
          resetRecaptcha();
          setStep(0);
        } else {
          setError(msg);
        }
      } else {
        setSubmissionStatus("pending");
        setStep(2);
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (landmarkerRef.current) {
      landmarkerRef.current.close();
      landmarkerRef.current = null;
    }
    setStep(0);
    setError("");
    setVerifyingRecaptcha(false);
    setCapturedImage(null);
    setFaceDetected(false);
    setCameraReady(false);
    setRecaptchaReady(false);
    setRecaptchaToken(null);
    setSubmissionStatus(null);
    setChallengeIndex(0);
    setChallengeOrder([...CHALLENGE_ORDER]);
    setAllChallengesDone(false);
    confirmCountRef.current = 0;
    cooldownRef.current = COOLDOWN_BETWEEN_CHALLENGES;
    setProgress(0);
    recaptchaRendered.current = false;
    resetRecaptcha();
    onClose?.();
  };

  // ── Shared button styles ───────────────────────────────────────────────────

  const secondaryBtnSx = {
    flex: 1, py: 1.2, borderRadius: "10px", fontWeight: 600, textTransform: "none",
    backgroundColor: "#ffffff", borderColor: "#0f172a", color: "#0f172a",
    "&:hover": { backgroundColor: "#f8fafc", borderColor: "#0f172a", color: "#0f172a" },
  };
  const primaryBtnSx = {
    flex: 1, py: 1.2, borderRadius: "10px", fontWeight: 700, textTransform: "none",
    bgcolor: "#047857", color: "#fff",
    "&:hover": { bgcolor: "#065f46", color: "#fff" },
    "&.Mui-disabled": { bgcolor: "#e2e8f0", color: "#64748b" },
  };

  const infoBoxSx = {
    mb: 2, p: 2, borderRadius: 2,
    bgcolor: "#ffffff", border: "1px solid #0f172a",
    textAlign: "center",
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <Box sx={modalBoxSx}>
        {/* Header */}
        <Box sx={{ backgroundColor: "#047857", color: "#fff", display: "flex", alignItems: "center", gap: 1.5, px: 3, py: 2 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: "50%", display: "grid", placeItems: "center", backgroundColor: "rgba(255,255,255,0.15)" }}>
            <VerifiedUserIcon fontSize="small" />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Alternative Verification
          </Typography>
        </Box>

        <Box sx={{ px: 4, pt: 3, pb: 4 }}>
          <Stepper activeStep={step} alternativeLabel sx={{ mb: 3 }}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel StepIconProps={{ sx: { "&.Mui-active": { color: "#047857" }, "&.Mui-completed": { color: "#047857" } } }}>
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          {/* reCAPTCHA container — stays in DOM but hidden when not on step 0 */}
          <Box id="manual-verify-recaptcha" sx={{
            display: step === 0 ? "flex" : "none",
            justifyContent: "center", my: step === 0 ? 3 : 0, minHeight: step === 0 ? 78 : 0,
          }} />

          {/* Step 0: reCAPTCHA */}
          {step === 0 && (
            <Box>
              <Typography sx={{ color: "#0f172a", mb: 2, lineHeight: 1.6 }}>
                Complete the security check below to proceed with alternative identity verification.
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: "flex", gap: 1.5 }}>
                <Button onClick={handleClose} variant="outlined" disabled={verifyingRecaptcha} sx={secondaryBtnSx}>Cancel</Button>
                <Button
                  onClick={handleContinueToLiveness}
                  variant="contained"
                  disabled={!recaptchaReady || verifyingRecaptcha}
                  startIcon={verifyingRecaptcha ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : null}
                  sx={primaryBtnSx}
                >
                  {verifyingRecaptcha ? "Verifying…" : "Continue"}
                </Button>
              </Box>
            </Box>
          )}

          {/* Step 1: Camera + Liveness Challenge */}
          {step === 1 && (
            <Box>
              {!capturedImage ? (
                <>
                  {/* Challenge instruction */}
                  {currentChallenge && !allChallengesDone && (
                    <Box sx={infoBoxSx}>
                      <Typography sx={{ fontSize: 13, color: "#0f172a", fontWeight: 600, mb: 0.5, letterSpacing: "0.05em" }}>
                        STEP {challengeIndex + 1} OF {totalChallenges}
                      </Typography>
                      <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#0f172a" }}>
                        {currentChallenge.label}
                      </Typography>
                      <Box sx={{ mt: 1.5, px: 2 }}>
                        <LinearProgress
                          variant="determinate"
                          value={progress}
                          sx={{
                            height: 8, borderRadius: 4,
                            bgcolor: "#e2e8f0",
                            "& .MuiLinearProgress-bar": {
                              bgcolor: progress >= 100 ? "#047857" : "#10b981",
                              borderRadius: 4,
                              transition: "transform 0.15s ease",
                            },
                          }}
                        />
                        <Typography sx={{ fontSize: 12, color: "#64748b", mt: 0.5 }}>
                          {progress < 100 ? "Follow the instruction above" : "Hold steady…"}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {allChallengesDone && !capturedImage && (
                    <Box sx={infoBoxSx}>
                      <CircularProgress size={24} sx={{ color: "#047857", mb: 0.5 }} />
                      <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>
                        Capturing your photo…
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ position: "relative", borderRadius: 2, overflow: "hidden", mb: 2, bgcolor: "#000" }}>
                    <Webcam
                      key={webcamKey}
                      ref={webcamRef}
                      audio={false}
                      screenshotFormat="image/jpeg"
                      mirrored
                      onUserMedia={() => setCameraReady(true)}
                      onUserMediaError={() => setError("Could not access camera. Check permissions.")}
                      style={{ width: "100%", display: "block", borderRadius: 8 }}
                      videoConstraints={{ facingMode: "user", width: 640, height: 480 }}
                    />
                    <canvas
                      ref={canvasRef}
                      style={{
                        position: "absolute", top: 0, left: 0,
                        width: "100%", height: "100%",
                        pointerEvents: "none",
                      }}
                    />

                    {/* Status pill */}
                    <Box sx={{
                      position: "absolute", top: 12, left: 12,
                      px: 1.5, py: 0.5, borderRadius: "20px",
                      bgcolor: faceDetected ? "rgba(4,120,87,0.85)" : "rgba(220,38,38,0.85)",
                      color: "#fff", fontSize: 13, fontWeight: 600,
                    }}>
                      {faceDetected ? "Face detected" : "No face detected"}
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", gap: 1.5 }}>
                    <Button
                      onClick={() => {
                        if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
                        if (landmarkerRef.current) {
                          landmarkerRef.current.close();
                          landmarkerRef.current = null;
                        }
                        setStep(0);
                        setCameraReady(false);
                        setFaceDetected(false);
                        setAllChallengesDone(false);
                        setChallengeIndex(0);
                        confirmCountRef.current = 0;
                        cooldownRef.current = COOLDOWN_BETWEEN_CHALLENGES;
                        setProgress(0);
                      }}
                      variant="outlined"
                      sx={secondaryBtnSx}
                    >
                      Back
                    </Button>
                    <Button
                      onClick={handleSubmit}
                      variant="contained"
                      disabled={!allChallengesDone || loading}
                      startIcon={loading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <CheckCircleOutlineIcon />}
                      sx={primaryBtnSx}
                    >
                      {loading ? "Submitting…" : "Submit Photo"}
                    </Button>
                  </Box>
                </>
              ) : (
                <>
                  <Box sx={infoBoxSx}>
                    <CheckCircleOutlineIcon sx={{ color: "#047857", fontSize: 28, mb: 0.5 }} />
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>
                      Liveness confirmed — review your photo below
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Box
                      component="img"
                      src={capturedImage}
                      alt="Captured"
                      sx={{ width: "100%", borderRadius: 2, display: "block" }}
                    />
                  </Box>
                  <Box sx={{ display: "flex", gap: 1.5 }}>
                    <Button onClick={handleRetake} variant="outlined" startIcon={<ReplayIcon />} sx={secondaryBtnSx}>
                      Retake
                    </Button>
                    <Button
                      onClick={handleSubmit}
                      variant="contained"
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <CheckCircleOutlineIcon />}
                      sx={primaryBtnSx}
                    >
                      {loading ? "Submitting..." : "Submit"}
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          )}

          {/* Step 2: Status */}
          {step === 2 && (
            <Box sx={{ textAlign: "center", py: 2 }}>
              {submissionStatus === "pending" && (
                <>
                  <CheckCircleOutlineIcon sx={{ fontSize: 56, color: "#047857", mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", mb: 1 }}>
                    Submitted for Review
                  </Typography>
                  <Typography sx={{ color: "#374151", lineHeight: 1.6, mb: 3 }}>
                    Your verification is being reviewed by our team. You'll be notified once it's approved.
                  </Typography>
                </>
              )}
              <Button
                onClick={() => {
                  handleClose();
                  navigate("/dashboard");
                }}
                variant="contained"
                sx={{ ...primaryBtnSx, flex: "unset", px: 5 }}
              >
                Go to Dashboard
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
}
